/**
 * The gap between "the connector connected" and "the client can see the turn it started".
 *
 * Connecting an app from the chat does not end the conversation: the oauth-complete endpoint hands
 * off to symphony-genome, which resolves the chat and wakes the assistant with `RunAssistantLoopAsync`.
 * That handoff is several seconds of network — verify the grant, publish `ConnectorChanged`, seed the
 * genome, then the first gateway iteration — and only the LAST of those steps stamps
 * `pendingPredictionId` on the conversation, which is the only thing `turn-activity` can read.
 *
 * So the client observes the connect (its own status poll flips the card to `connected`) well before
 * it observes the turn, and in between it renders a dead screen at exactly the moment the user has
 * just come back from a browser tab expecting something to happen. This module covers that window:
 * seeing the connect arms a local busy state, so the default thinking line shows immediately and the
 * real one from the harness replaces it as soon as a THINKING row streams in.
 *
 * Shared 1:1 with iOS `ConnectHandoff.swift` and Android `ConnectHandoff.kt` — same constant, same
 * rule, same test table. Change it here and change it there.
 */

/**
 * How long the connect alone is allowed to mean "busy".
 *
 * It is a bridge to a real signal, not a substitute for one: a genome that never greets (the connect
 * repaired an already-known toolkit, the greeting was suppressed, the service is down) must not leave
 * a spinner running forever. Long enough to outlast the handoff, short enough that a silent failure
 * reads as "nothing happened" rather than as a hang.
 */
export const CONNECT_HANDOFF_GRACE_MS = 45 * 1000;

/**
 * How long the immediate "Inspecting your {name}." placeholder holds before it swaps to the generic
 * "Analyzing your data…" line, while the real thinking is still on its way.
 *
 * Same sequence, same constant as iOS/Android `ConnectHandoff`: the first line names the connector the
 * user just linked, so the wake reads as work on THAT app; after this it generalises, because the genome
 * is past the connect and into the data by then. Both lines are only ever shown until the first REAL
 * streamed thinking line takes over. Change it here and change it there.
 */
export const HANDOFF_ANALYZING_DELAY_MS = 4 * 1000;

/** The two placeholder phases, in order. */
export type HandoffPhase = 'inspecting' | 'analyzing';

/**
 * The i18n key for the post-connect placeholder thinking line at a given phase — or `null` when nothing
 * should show, so the indicator falls back to the generic "Thinking…".
 *
 * Kept pure and here (rather than inline in the hook) so the resolution order is unit-tested without the
 * React/i18n graph. The rule: the placeholder shows only while the handoff is `pending` AND no real
 * streamed thinking is up (real thinking always outranks it), and "inspecting" needs a connector name to
 * interpolate — without one it yields to the generic line rather than rendering "Inspecting your .".
 */
export function handoffPlaceholderKey(
  phase: HandoffPhase | null,
  opts: { pending: boolean; realThinkingActive: boolean; hasName: boolean },
): 'chat.thinking.inspecting' | 'chat.thinking.analyzing' | null {
  if (phase === null || !opts.pending || opts.realThinkingActive) return null;
  if (phase === 'inspecting') return opts.hasName ? 'chat.thinking.inspecting' : null;
  return 'chat.thinking.analyzing';
}

/** Whether a connect observed at `armedAt` still counts as a turn about to start. */
export function connectHandoffPending(armedAt: number | null, now: number = Date.now()): boolean {
  if (armedAt === null) return false;
  // A clock-skewed future stamp is still live — only the past can expire.
  return now - armedAt < CONNECT_HANDOFF_GRACE_MS;
}

/**
 * How recently this browser must have tapped Connect for an already-connected card to count as
 * "the connect just happened here".
 *
 * Wider than the grace above, because it measures a different thing: the grace bounds how long a
 * spinner may run, while this bounds how long ago the user started the flow. Authorizing in another
 * tab takes as long as it takes — reading a consent screen, picking an account, a password prompt —
 * and the tap marker is the only record that it was THIS person, in THIS browser.
 */
export const CONNECT_TAP_RECENT_MS = 10 * 60 * 1000;

/**
 * Should the card's newly-observed `connected` arm the handoff?
 *
 * BOTH a live false→true transition and a first-read-already-connected arm, but ONLY on evidence that
 * the connect belongs to this person and is recent: `tappedAt` is when THIS browser tapped Connect on
 * THIS card, persisted in localStorage so it survives the reload a transition does not, and
 * one-shot-cleared the moment the handoff arms.
 *
 * The first read is the case that first needed fixing (coming back from the authorization tab and
 * reloading resets the ref, so the first read is already connected with no transition left to observe —
 * the dead screen this module exists to prevent). The transition path then needed the SAME guard: on a
 * fresh page load the card can observe `connected` go false→true as the connectors list loads — a reload,
 * not a connect made here — and arming there re-showed the placeholder over a turn that already ran. The
 * tap marker is the one piece of evidence that separates a real connect from a reload, so both paths
 * require it. Opening an old thread spins nothing — its tap marker is long gone, or absent because the
 * connect happened elsewhere.
 */
export function shouldArmConnectHandoff(
  previous: boolean | undefined,
  connected: boolean,
  tappedAt: number | null = null,
  now: number = Date.now(),
): boolean {
  if (!connected) {
    return false;
  }
  // Already-known-connected across renders is not a new connect.
  if (previous === true) {
    return false;
  }
  return tappedAt !== null && now - tappedAt < CONNECT_TAP_RECENT_MS;
}
